import { useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';
import { ReclamationAccessPolicy } from 'graphql-ts-types';

import { MessageType } from 'common/enums/Alert';
import type { Locale } from 'common/enums/Locale';
import { executeQuery } from 'common/gql';
import type { AdditionalInfoQueryVariables } from 'common/gql/generated/schema';
import { toGQLLocale } from 'common/gql/utils/gql-locale';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { DispatchState } from 'common/types/React';
import additionalInfoQuery from '../../gql/query/additionalInfo.gql';
import type { AdditionalInfoQueryResponse } from '../../gql/query/types';
import type { StepSubmitHandler } from '../../types/stepSubmitHandler';
import { hasEmptyPartNumber } from '../../utils/additional-feedback';
import { ReclamationError } from '../../utils/ReclamationError';
import type { AdditionalInfoTrackingEventData } from '../../utils/tracking';
import { dispatchSubmitStepEvent } from '../../utils/tracking';
import { ValidationError } from '../../utils/ValidationError';
import type { DrawerAlert } from '../alert';
import { useObserveAlert } from '../index';
import { getUpdateReclamationVariables, updateReclamation } from '../update-reclamation';

type SetPartNumbers = DispatchState<string[]>;
type SetString = DispatchState<string>;
type SetIsFetching = DispatchState<boolean>;
type SetAssemblyInstruction = DispatchState<string>;

interface UseAdditionalInfoOptions {
  reclamationId: string;
  itemId: string;
  alert: DrawerAlert;
  isPictureGuideOpen: boolean;
  pictureCount: number;
  trackingEventData: AdditionalInfoTrackingEventData;
  setIsNextDisabled: (value: boolean) => void;
  setIsFooterVisible: (value: boolean) => void;
  resetHeaderProps: () => void;
  setOnSubmit: DispatchState<StepSubmitHandler>;
  setSelectedReason: DispatchState<string>;
}

interface UseSetSubmitHandlerOptions {
  partNumbers: string[];
  comment: string;
  reclamationId: string;
  itemId: string;
  isImageRequired: boolean;
  isPartNumberRequired: boolean;
  isCommentRequired: boolean;
  pictureCount: number;
  vendorOrderId: string;
  isVendorOrderIdRequired: boolean;
  trackingEventData: AdditionalInfoTrackingEventData;
  setIsFetching: SetIsFetching;
  setIsNextDisabled: (value: boolean) => void;
  setOnSubmit: DispatchState<StepSubmitHandler>;
}

interface UseAdditionalInfoResult {
  isImageAllowed: boolean;
  partNumbersAllowed: boolean;
  partNumbers: string[];
  commentPolicy: ReclamationAccessPolicy;
  comment: string;
  isFetching: boolean;
  internalAlert: DrawerAlert;
  assemblyInstruction: string;
  partNumberPolicy: ReclamationAccessPolicy;
  setPartNumbers: SetPartNumbers;
  setComment: SetString;
  vendorOrderId: string;
  setVendorOrderId: SetString;
  vendorOrderIdPolicy: ReclamationAccessPolicy;
}

interface UseFetchAdditionalInfoOptions {
  reclamationId: string;
  itemId: string;
  setPartNumbers: SetPartNumbers;
  setComment: SetString;
  setVendorOrderId: SetString;
  setInternalAlert: DispatchState<DrawerAlert>;
  setIsFetching: SetIsFetching;
  setAssemblyInstruction: SetAssemblyInstruction;
  setSelectedReason: DispatchState<string>;
}

type UseAdditionalInfo = (options: UseAdditionalInfoOptions) => UseAdditionalInfoResult;

type UseSetSubmitHandler = (options: UseSetSubmitHandlerOptions) => void;

const fetchAdditionalInfo = async (locale: Locale, reclamationId: string, itemId: string) =>
  await executeQuery<AdditionalInfoQueryResponse, AdditionalInfoQueryVariables>({
    query: additionalInfoQuery,
    variables: {
      locale: toGQLLocale(locale),
      reclamationId,
      itemId,
    },
    useApq: true,
  });

export const useErrorMessage = () => {
  const [message, setMessage] = useState<Nullable<string>>(null);
  const { t } = useLocalization();

  const setErrorMessage = () => {
    setMessage(t('h24_something_went_wrong'));
  };

  const resetErrorMessage = () => {
    setMessage(null);
  };

  return [message, setErrorMessage, resetErrorMessage] as const;
};

const useSetSubmitHandler: UseSetSubmitHandler = ({
  reclamationId,
  itemId,
  partNumbers,
  comment,
  pictureCount,
  isImageRequired,
  isPartNumberRequired,
  isCommentRequired,
  vendorOrderId,
  isVendorOrderIdRequired,
  trackingEventData,
  setIsFetching,
  setIsNextDisabled,
  setOnSubmit,
}) => {
  const { t } = useLocalization();
  const locale = useGQLLocale();

  useEffect(() => {
    let isAlive = true;

    setOnSubmit(
      // eslint-disable-next-line complexity
      () => async () => {
        if (!isAlive) {
          return;
        }

        if (isImageRequired && !pictureCount) {
          throw new ReclamationError({
            message: t('h24_please_insert_picture_to_proceed'),
            type: MessageType.Notice,
          });
        }

        const hasCommentError = isCommentRequired && comment === '';
        const hasVendorOrderIdError = isVendorOrderIdRequired && vendorOrderId === '';
        const hasPartNumberError = isPartNumberRequired && hasEmptyPartNumber(partNumbers);

        if (hasCommentError || hasVendorOrderIdError || hasPartNumberError) {
          throw new ValidationError();
        }

        setIsFetching(true);
        setIsNextDisabled(true);

        await updateReclamation(
          getUpdateReclamationVariables({
            locale,
            reclamationId,
            itemId,
            partNumbers: partNumbers[0] ? partNumbers : null,
            comment: comment || null,
            vendorOrderId: vendorOrderId || null,
          })
        );

        dispatchSubmitStepEvent(trackingEventData);
        setIsFetching(false);
      }
    );

    return () => {
      isAlive = false;
    };
  }, [
    reclamationId,
    itemId,
    partNumbers,
    comment,
    setOnSubmit,
    setIsNextDisabled,
    isImageRequired,
    isPartNumberRequired,
    isCommentRequired,
    pictureCount,
    vendorOrderId,
    isVendorOrderIdRequired,
  ]);
};

const useFetchAdditionalInfo = ({
  reclamationId,
  itemId,
  setPartNumbers,
  setComment,
  setInternalAlert,
  setIsFetching,
  setAssemblyInstruction,
  setVendorOrderId,
  setSelectedReason,
}: UseFetchAdditionalInfoOptions) => {
  const [attachmentPolicy, setAttachmentPolicy] = useState(ReclamationAccessPolicy.Disallowed);
  const [partNumberPolicy, setPartNumberPolicy] = useState(ReclamationAccessPolicy.Disallowed);
  const [commentPolicy, setCommentPolicy] = useState(ReclamationAccessPolicy.Disallowed);
  const [vendorOrderIdPolicy, setVendorOrderIdPolicy] = useState(
    ReclamationAccessPolicy.Disallowed
  );

  const { t, locale } = useLocalization();

  useEffect(() => {
    (async () => {
      setIsFetching(true);
      try {
        const { data } = await fetchAdditionalInfo(locale, reclamationId, itemId);

        if (data) {
          setAttachmentPolicy(data.attachmentPolicy);
          setPartNumberPolicy(data.partNumberPolicy);
          setCommentPolicy(data.commentPolicy);
          setComment(data.comment);
          setAssemblyInstruction(data.article.resources[0]?.assemblyInstructionPath);
          setVendorOrderId(data.vendorOrderId);
          setVendorOrderIdPolicy(data.vendorOrderIdPolicy);
          setSelectedReason(data.reason?.id ?? '');

          if (data.partNumbers[0]) {
            setPartNumbers(data.partNumbers);
          }
        }
      } catch {
        setInternalAlert({ message: t('h24_something_went_wrong') });
      } finally {
        setIsFetching(false);
      }
    })();
  }, [reclamationId, itemId]);

  return {
    attachmentPolicy,
    partNumberPolicy,
    commentPolicy,
    vendorOrderIdPolicy,
  };
};

/* eslint-disable max-lines */
export const useAdditionalInfo: UseAdditionalInfo = ({
  reclamationId,
  itemId,
  alert,
  isPictureGuideOpen,
  pictureCount,
  trackingEventData,
  setIsNextDisabled,
  setIsFooterVisible,
  resetHeaderProps,
  setOnSubmit,
  setSelectedReason,
}) => {
  const [partNumbers, setPartNumbers] = useState(['']);
  const [comment, setComment] = useState('');
  const [vendorOrderId, setVendorOrderId] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [internalAlert, setInternalAlert] = useState(alert);
  const [assemblyInstruction, setAssemblyInstruction] = useState('');

  const { attachmentPolicy, partNumberPolicy, commentPolicy, vendorOrderIdPolicy } =
    useFetchAdditionalInfo({
      reclamationId,
      itemId,
      setPartNumbers,
      setComment,
      setInternalAlert,
      setIsFetching,
      setAssemblyInstruction,
      setVendorOrderId,
      setSelectedReason,
    });
  const isImageRequired = attachmentPolicy === ReclamationAccessPolicy.Required;
  const isCommentRequired = commentPolicy === ReclamationAccessPolicy.Required;
  const isPartNumberRequired = partNumberPolicy === ReclamationAccessPolicy.Required;
  const isVendorOrderIdRequired = vendorOrderIdPolicy === ReclamationAccessPolicy.Required;

  useSetSubmitHandler({
    reclamationId,
    itemId,
    partNumbers,
    comment,
    isImageRequired,
    isPartNumberRequired,
    isCommentRequired,
    pictureCount,
    vendorOrderId,
    isVendorOrderIdRequired,
    trackingEventData,
    setIsFetching,
    setIsNextDisabled,
    setOnSubmit,
  });

  useEffect(() => {
    if (!isPictureGuideOpen) {
      resetHeaderProps();
      setIsFooterVisible(true);
    }
  }, [isPictureGuideOpen]);

  useObserveAlert(alert, setInternalAlert);

  useEffect(() => {
    if (alert && pictureCount) {
      setInternalAlert(null);
    }
  }, [pictureCount]);

  useEffect(() => {
    const hasImageError = isImageRequired && !pictureCount;
    const hasCommentError = isCommentRequired && (comment === '' || comment.length < 50);
    const hasVendorOrderIdError = isVendorOrderIdRequired && vendorOrderId === '';
    const hasPartNumberError = isPartNumberRequired && hasEmptyPartNumber(partNumbers);

    if (hasImageError || hasCommentError || hasVendorOrderIdError || hasPartNumberError) {
      setIsNextDisabled(true);
    } else {
      setIsNextDisabled(false);
    }
  }, [
    isImageRequired,
    isCommentRequired,
    isPartNumberRequired,
    isVendorOrderIdRequired,
    pictureCount,
    comment,
    partNumbers,
    vendorOrderId,
  ]);

  return {
    isImageAllowed: attachmentPolicy !== ReclamationAccessPolicy.Disallowed,
    partNumbersAllowed: partNumberPolicy !== ReclamationAccessPolicy.Disallowed,
    partNumbers,
    comment,
    isFetching,
    internalAlert,
    assemblyInstruction,
    partNumberPolicy,
    commentPolicy,
    setPartNumbers,
    setComment,
    vendorOrderId,
    setVendorOrderId,
    vendorOrderIdPolicy,
  };
};
